import React, { useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Sparkles, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const USER_TYPES = [
  { value: 'carrier', label: 'Carrier / Owner-Operator' },
  { value: 'broker',  label: 'Freight Broker' },
  { value: 'shipper', label: 'Shipper / Enterprise' },
];

const WaitlistModal = ({ open, onOpenChange, defaultType = 'carrier', triggerLabel }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [userType, setUserType] = useState(defaultType);
  const [company, setCompany] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (!name.trim() || !email.trim()) {
      return toast.error('Name and email are required');
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/waitlist`, {
        name: name.trim(), email: email.trim(), user_type: userType,
        company: company.trim(),
      });
      setDone(true);
      toast.success("You're on the list. We'll email you the moment we go live.");
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to join waitlist');
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    onOpenChange?.(false);
    // Reset after close animation
    setTimeout(() => {
      setDone(false); setName(''); setEmail(''); setCompany('');
      setUserType(defaultType);
    }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => o ? onOpenChange?.(true) : close()}>
      <DialogContent className="max-w-md" data-testid="waitlist-modal">
        {done ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-emerald-400">
                <CheckCircle2 className="w-5 h-5" /> You're on the list
              </DialogTitle>
              <DialogDescription>
                We'll email <strong>{email}</strong> the moment {triggerLabel || 'this tier'} goes live.
                Founding {USER_TYPES.find(u => u.value === userType)?.label.toLowerCase()} accounts get
                priority + locked-in pricing.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button onClick={close} data-testid="waitlist-close-btn">Close</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-amber-400" /> Reserve Your Spot
              </DialogTitle>
              <DialogDescription>
                We're opening to the public after our Stripe and AWS approvals.
                Join the waitlist — we'll notify you the moment {triggerLabel || 'pricing'} goes live.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 pt-2">
              <div className="space-y-1">
                <Label className="text-xs">Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)}
                       placeholder="Your full name" data-testid="waitlist-name" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Email</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                       placeholder="you@example.com" data-testid="waitlist-email" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">I am a…</Label>
                <div className="grid grid-cols-3 gap-2" data-testid="waitlist-user-type-row">
                  {USER_TYPES.map((u) => (
                    <button
                      key={u.value}
                      onClick={() => setUserType(u.value)}
                      className={`px-2 py-2 text-xs rounded-md border transition
                        ${userType === u.value
                          ? 'border-amber-500 bg-amber-500/10 text-amber-300'
                          : 'border-zinc-700 hover:border-zinc-500 text-zinc-300'}`}
                      data-testid={`waitlist-type-${u.value}`}
                    >
                      {u.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Company (optional)</Label>
                <Input value={company} onChange={(e) => setCompany(e.target.value)}
                       placeholder="ACME Trucking LLC" data-testid="waitlist-company" />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={close}>Cancel</Button>
              <Button onClick={submit} disabled={submitting || !name.trim() || !email.trim()}
                      data-testid="waitlist-submit-btn">
                {submitting
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Joining…</>
                  : 'Reserve my spot'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WaitlistModal;
